import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Injector,
    inject,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { IForum } from '@fi-shared/fi-forum/fi-forum';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { AccessRightType, ApplicationSettingsType, ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { IItemsListAction2, ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { FiForumSharedService } from '@fi-shared/fi-forum/fi-forum-shared.service';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { FiCommentsService } from '@fi-shared/fi-comments/fi-comments.service';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { FiFavoritesService } from '@fi-shared/fi-shared-services/fi-favorites.service';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';

@Component({
    selector: 'fi-forum-list',
    templateUrl: './fi-forum-list.component.html',
    styleUrls: ['./fi-forum-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-forum-list' },
    standalone: false,
})
export class FiForumListComponent extends FiRemoteStateFullDataSource<IForum> implements OnInit {
    public readonly defaultSortColumnName = 'LatestActivity';
    public readonly defaultSortDirection = true;
    public readonly objectType = ObjectTypes.Forum;
    public readonly itemsListType = FiItemsListType.ForumListData;

    private accessToApplicationTypeService = inject(AccessToApplicationTypeService);
    private forumService = inject(FiForumSharedService);
    private fiSignalRService = inject(FiSignalRService);
    private fiSignalRHelperService = inject(FiSignalRHelperService);
    private fiFavoritesService = inject(FiFavoritesService);
    private itemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    private detectDeviceService = inject(DetectDevicesService);
    private commentsService = inject(FiCommentsService);

    protected readonly sideListOptions: ISideListOptionsItem[] = this.getSideListOptions();
    protected readonly actions: IItemsListAction2[] = [
        {
            title: this.appTextService.get('forum.edit_forum'),
            onClick: (item: IForum) => this.fiStateService.go(FiStateNames.Forum.Edit, { id: item.Id }),
            disabled: (item: IForum) => !item.HasEditAccess,
        },
        {
            title: this.appTextService.get('forum.delete_forum'),
            onClick: (item: IForum) => this.onDeleteForumClick(item),
            disabled: (item: IForum) => !item.HasEditAccess,
        },
        {
            title: this.appTextService.get('common.add_to_favorites'),
            onClick: (item: IForum) => {
                this.fiFavoritesService
                    .saveFavorite(item.Id, ObjectTypes.Forum, true)
                    .then(() => (item.IsFavorite = !item.IsFavorite));
            },
            condition: (item: IForum) => !item.IsFavorite,
        },
        {
            title: this.appTextService.get('common.remove_from_favorites'),
            onClick: (item: IForum) => {
                this.fiFavoritesService.deleteFavorite(item.Id, ObjectTypes.Forum, true).then(() => {
                    item.IsFavorite = !item.IsFavorite;
                    if (this.filterType === SideListOptionType.Favorite) {
                        this.removeData([item]);
                    }
                });
            },
            condition: (item: IForum) => item.IsFavorite,
        },
        // todo: will be added later https://chainformation.atlassian.net/browse/FI-4497
        // {
        //     title: this.appTextService.get('post.mark_as_read'),
        //     onClick: () => {
        //         this.fiLoading.trackPromise(this.forumService.markForumPostsAsVisitedByForumIds(this.selectedItems.map(forum => forum.Id)))
        //             .then(forumPostIdsMarkedAsSeen => {
        //                 this.fiSignalRHelperService.onMarkForumPostsAsVisited(forumPostIdsMarkedAsSeen)
        //                 this.selectedItems.forEach(item => {
        //                     item.NewForumPostsCount = 0;
        //                 });
        //                 this.clearSelection();
        //                 this.cdr.markForCheck();
        //             });
        //     },
        //     disabled: () => !this.selectedItems.find(item => item.NewForumPostsCount > 0)
        // }
    ];
    protected readonly isAdminRights: boolean = this.accessToApplicationTypeService.HasAccessToApplicationType(
        ApplicationSettingsType.ForumAdmin,
        AccessRightType.Read,
    );

    @HostBinding('class.mobile-view') get isMobileView(): boolean {
        return this.detectDeviceService.isMobilePhone();
    }

    protected readonly FiStateNames = FiStateNames;
    protected readonly FiProfileSizes = FiProfileSizes;

    constructor(injector: Injector) {
        super(injector);
    }

    ngOnInit(): void {
        this.setLoading();

        this.fiSignalRService.commentsUpdatedEvent.subscribe((data) => {
            if (data.ObjectType === ObjectTypes.ForumPost) {
                const items = this.items();
                this.commentsService
                    .getCommentsCount({
                        forumIds: items.map((item) => item.Id),
                        objectTypes: [ObjectTypes.ForumPost],
                    })
                    .then((commentsCountData) => {
                        this.updateItems((forums) =>
                            forums.map((forum) => {
                                const commentsCount = commentsCountData
                                    .filter(
                                        (item) =>
                                            item.ForumId === forum.Id && item.ObjectType === ObjectTypes.ForumPost,
                                    )
                                    .reduce((sum, item) => sum + item.Count, 0);

                                return {
                                    ...forum,
                                    CommentsCount: commentsCount,
                                };
                            }),
                        );
                        this.cdr.detectChanges();
                    });
            }
        }, this.ngOnDestroy$);
    }

    private setLoading() {
        /*
        this.isLocalLoadingHandler = true;
        this._loading$.next(true);
*/
    }

    private getSideListOptions(): ISideListOptionsItem[] {
        // show "all forums" filter only for global admins
        // https://chainformation.atlassian.net/browse/FI-5519
        const isGlobalAdminRights = this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.ForumAdmin,
            AccessRightType.Admin,
        );

        let sideListOptions: ISideListOptionsItem[] = [];
        if (isGlobalAdminRights) {
            sideListOptions = [
                {
                    value: SideListOptionType.All,
                    text: this.appTextService.get('forum.all_forums'),
                },
            ];
        }
        sideListOptions = [
            ...sideListOptions,
            ...[
                { value: SideListOptionType.ForumSharedToMe, text: this.appTextService.get('forum.shared_to_me') },
                { value: SideListOptionType.ForumNewPosts, text: this.appTextService.get('forum.new_posts') },
                { value: SideListOptionType.CreatedBy, text: this.appTextService.get('common.created_by_me') },
                { value: SideListOptionType.Favorite, text: this.appTextService.get('common.my_favorites') },
            ],
        ];

        return sideListOptions;
    }

    private onDeleteForumClick(item: IForum): void {
        this.itemsDeleteHelperService
            .forumDeleteConfirm([item])
            .then((itemsToDelete) => super.doDeleteItems(itemsToDelete));
    }

    public loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        return this.forumService.getForumsListData(parameter);
    }

    protected deleteFn(itemsToDelete: IForum[]): IFiHttpPromise<void> {
        return this.forumService.deleteForums(itemsToDelete.map((forum) => forum.Id));
    }

    protected createNewForumPost(): void {
        this.forumService.createNewForumPost(this.isMobileView, 0);
    }
}
